import { createJurisdictionHeaders } from '@/data';
import { URLS } from '@/data/config';
import { LOG_TAG_ACCOUNT_DETAILS_UPDATE } from '@/feature/verify-phone-number/utils/constants';
import { logger } from '@/utils/logging';
import { useQuery } from '@tanstack/react-query';

type AvailableAvatarsResponse = {
    data: {
        avatar_url: string;
        all_avatar_urls: string[];
    };
};

const fetchAvailableAvatars = async (): Promise<AvailableAvatarsResponse> => {
    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/user/avatars`, {
        method: 'GET',
        headers: {
            ...createJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
    });

    let responseData;

    try {
        responseData = await response.json();
    } catch (error) {
        logger.warn(LOG_TAG_ACCOUNT_DETAILS_UPDATE, 'Failed to parse response JSON for fetchAvailableAvatars', error);
        responseData = null;
    }

    if (!response.ok) {
        logger.warn(LOG_TAG_ACCOUNT_DETAILS_UPDATE, 'Error fetching available avatars', responseData);
        throw new Error(responseData?.message || 'Error fetching available avatars');
    }

    return responseData;
};

export const availableAvatarsKeys = {
    all: ['available-avatars'] as const,
};

/**
 * Custom react query hook to fetch available avatars
 * GET /auth/user/avatars
 */
export const useAvailableAvatars = () => {
    return useQuery({
        queryKey: availableAvatarsKeys.all,
        queryFn: fetchAvailableAvatars,
        select: data => ({
            ...data.data,
            all_avatar_urls: [...data.data.all_avatar_urls].sort((a, b) =>
                a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
            ),
        }),
    });
};
